import { Army } from "@/domain/army";
import { getImageUploadSrc } from "@/domain/s3-urls";
import { CreateArmyData } from "@/lib/db";
import { id } from "@/lib/nanoid";
import { pluralize } from "@/lib/text";
import { UnitData } from "@/schema/latest";
import { FormDialogV2 } from "@/ui/form-dialog";
import { FormTextField } from "@/ui/inputs";
import { InputsGroup } from "@/ui/inputs/group";
import { Button, FormSubmit, useDialogContext, useStoreState } from "@ariakit/react";
import { compact } from "es-toolkit";
import { useReducer } from "react";
import { wrap } from "../army-form-dialog/wrap";
import { useDatabase } from "../database-provider";
import { harbourLight } from "../fonts.css";
import { useForm } from "../hooks/use-form";
import { useNavigate } from "../hooks/use-navigate";
import { Image } from "../image";
import { BattleStandardMiniIcon, GeneralMiniIcon } from "../mini-icon";
import { yellowTheme } from "../utils.css";
import { armySummary, counter, unitPicker } from "./style.css";

function ArmySummary(props: {
  name: string;
  summary: string;
  generalSelected: boolean;
  battleStandardSelected: boolean;
}) {
  const { name, summary, generalSelected, battleStandardSelected } = props;

  return (
    <div className={armySummary.container}>
      <div className={armySummary.content}>
        <div className={armySummary.heading}>{name}</div>
        <div>
          {summary}{" "}
          <GeneralMiniIcon className={armySummary.icon({ isSelected: generalSelected })} />
          <BattleStandardMiniIcon
            className={armySummary.icon({ isSelected: battleStandardSelected })}
          />
        </div>
      </div>

      <FormSubmit className={armySummary.submit} style={yellowTheme}>
        Muster Army
      </FormSubmit>
    </div>
  );
}

function UnitCounter(props: {
  onDecrement: () => void;
  onIncrement: () => void;
  unitCount: number;
}) {
  const { unitCount, onDecrement, onIncrement } = props;

  return (
    <div className={counter.container}>
      <Button disabled={unitCount < 1} onClick={onDecrement} className={counter.button}>
        -
      </Button>
      <div>{unitCount}</div>
      <Button onClick={onIncrement} className={counter.button}>
        +
      </Button>
    </div>
  );
}

type State = {
  counts: Record<string, number>;
  generalUnitId: string | null;
  battleStandardUnitId: string | null;
};

type Action =
  | { type: "TOGGLE_GENERAL"; unitId: string }
  | { type: "TOGGLE_BATTLE_STANDARD"; unitId: string }
  | { type: "INCREMENT_UNIT_COUNT"; unitId: string; value: number };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "TOGGLE_GENERAL": {
      const togglingOn = state.generalUnitId !== action.unitId;
      return {
        ...state,
        generalUnitId: togglingOn ? action.unitId : null,
        counts:
          togglingOn && state.counts[action.unitId] === 0 ?
            { ...state.counts, [action.unitId]: 1 }
          : state.counts,
      };
    }
    case "TOGGLE_BATTLE_STANDARD": {
      const togglingOn = state.battleStandardUnitId !== action.unitId;
      return {
        ...state,
        battleStandardUnitId: togglingOn ? action.unitId : null,
        counts:
          togglingOn && state.counts[action.unitId] === 0 ?
            { ...state.counts, [action.unitId]: 1 }
          : state.counts,
      };
    }
    case "INCREMENT_UNIT_COUNT": {
      const newCount = (state.counts[action.unitId] ?? 0) + action.value;
      const isGeneral = state.generalUnitId === action.unitId;
      const isBattleStandard = state.battleStandardUnitId === action.unitId;
      return {
        ...state,
        counts: { ...state.counts, [action.unitId]: newCount },
        generalUnitId: newCount < 1 && isGeneral ? null : state.generalUnitId,
        battleStandardUnitId: newCount < 1 && isBattleStandard ? null : state.battleStandardUnitId,
      };
    }
  }
}

function useMusterArmyState(collection: Army) {
  const generalUnitId = collection.units.find((u) => u.hasKeyword("general"))?.id ?? null;
  const battleStandardUnitId =
    collection.units.find((u) => u.hasKeyword("battle_standard"))?.id ?? null;

  return useReducer(reducer, {
    counts: Object.fromEntries(
      collection.units.map((unit) => [
        unit.id,
        unit.id === generalUnitId || unit.id === battleStandardUnitId ? 1 : 0,
      ]),
    ),
    generalUnitId: generalUnitId,
    battleStandardUnitId: battleStandardUnitId,
  });
}

function toArmyData(values: {
  name: string;
  description: string;
  generalUnitId: string | null;
  battleStandardUnitId: string | null;
  counts: Record<string, number>;
  collection: Army;
}) {
  const { name, description, generalUnitId, battleStandardUnitId, counts, collection } = values;

  const armyData: CreateArmyData = {
    name,
    description,
    ruleset_version: collection.ruleset.version,
    type: "ARMY",
    images: collection.data.images,
    posterImages: collection.data.posterImages,
    units: collection.units.flatMap((unit) => {
      const unitCount = counts[unit.id] ?? 0;

      // If no units of this type were selected, drop this profile.
      if (unitCount < 1) {
        return [];
      }

      // Strip any collection-level general/battle standard keywords. Those selected during
      // mustering take precedence.
      const keywords = unit.data.keywords.filter(
        (k) => k.id !== "general" && k.id !== "battle_standard",
      );
      const isGeneral = generalUnitId === unit.id;
      const isBattleStandard = battleStandardUnitId === unit.id;
      const isWizard = keywords.some((k) => k.id === "wizard");

      const unitData: UnitData = {
        ...unit.data,
        id: id(),
        count: unitCount,
        keywords: compact([
          isGeneral && { id: "general" },
          isBattleStandard && { id: "battle_standard" },
          ...keywords,
        ]),
        cursed_artefacts: isGeneral ? unit.data.cursed_artefacts : null,
        abyssal_allegiances: isGeneral || isWizard ? unit.data.abyssal_allegiances : null,
      };

      // If a unit includes a general or battle standard, split the unit into one with
      // the special keywords and the rest without.
      const overflowCount = isGeneral || isBattleStandard ? unitCount - 1 : 0;
      if (overflowCount > 0) {
        return [
          { ...unitData, count: 1, id: id() },
          {
            ...unitData,
            count: overflowCount,
            keywords,
            cursed_artefacts: null,

            // A unit can be a Wizard (and therefore have Abyssal Allegiances) using Cursed
            // Artefacts. This means that when we split the unit and it loses it's CAs, it
            // will become invalid. We therefore check for the presence of the explicit
            // wizard keyword, only include AAs in the split-off unit if those are present.
            abyssal_allegiances: isWizard ? unitData?.abyssal_allegiances : null,
          },
        ];
      }

      return unitData;
    }),
  };

  return armyData;
}

export function MusterArmyDialog(props: { collection: Army }) {
  const { collection } = props;

  const dialog = useDialogContext();

  const [{ counts, generalUnitId, battleStandardUnitId }, dispatch] =
    useMusterArmyState(collection);

  const database = useDatabase();
  const navigate = useNavigate();
  const { form, submitName } = useForm({
    defaultValues: {
      name: "",
    },
    async onSubmit({ values }) {
      return await wrap(
        database.createArmy(
          toArmyData({
            name: values.name || collection.data.name,
            description: "",
            counts,
            generalUnitId,
            battleStandardUnitId,
            collection,
          }),
        ),
      );
    },
    onSuccess(id) {
      navigate(`~/army/a/${id}`);
      dialog?.hide();
    },
  });

  const totalCost = collection.units.reduce(
    (sum, unit) => sum + unit.points * (counts[unit.id] ?? 0),
    0,
  );

  const generalSelected = !!generalUnitId && !!counts[generalUnitId];
  const battleStandardSelected = !!battleStandardUnitId && !!counts[battleStandardUnitId];

  const name = useStoreState(form, (s) => s.values.name);
  const totalUnitsCount = Object.values(counts).reduce((sum, value) => sum + value, 0);

  return (
    <FormDialogV2
      form={form}
      heading="Muster Army"
      formSubmitName={submitName}
      submitLabel={
        <ArmySummary
          name={name || collection.name}
          summary={
            totalUnitsCount === 0 ? `No units selected` : (
              `${totalCost}pts, ${pluralize(totalUnitsCount, "unit", "units")} `
            )
          }
          generalSelected={generalSelected}
          battleStandardSelected={battleStandardSelected}
        />
      }
    >
      <p style={{ marginBlockEnd: "1.5rem" }}>
        Muster a new army using units from this collection. Select at least one unit and optionally
        assign <GeneralMiniIcon /> General and <BattleStandardMiniIcon /> Battle Standard.
      </p>
      <InputsGroup>
        <FormTextField
          name={form.names.name}
          label="Name"
          placeholder={collection.name}
          hint="Optionally, choose a name for the new army."
          data-1p-ignore
        />

        <div>
          <div className={unitPicker.label}>Units</div>

          <div className={unitPicker.container}>
            {collection.units.map((unit) => {
              const unitCount = counts[unit.id];

              return (
                <div key={unit.id} className={unitPicker.unit}>
                  <div className={unitPicker.unitContent}>
                    <div className={unitPicker.unitHeader}>
                      {unit.image && (
                        <Image
                          src={getImageUploadSrc(unit.image.src)}
                          width={800}
                          height={450}
                          optimize
                          className={unitPicker.unitImage}
                        />
                      )}

                      <div>
                        <div className={harbourLight}>{unit.name}</div>
                        <div style={{ fontSize: "0.875rem", marginBlockStart: "0.125rem" }}>
                          {unit.summary}
                        </div>
                      </div>
                    </div>

                    <div className={unitPicker.unitActions}>
                      <div className={unitPicker.unitCounter}>
                        <UnitCounter
                          unitCount={unitCount}
                          onIncrement={() => {
                            dispatch({ type: "INCREMENT_UNIT_COUNT", unitId: unit.id, value: 1 });
                          }}
                          onDecrement={() => {
                            dispatch({ type: "INCREMENT_UNIT_COUNT", unitId: unit.id, value: -1 });
                          }}
                        />
                      </div>

                      <Button
                        className={unitPicker.unitKeywordButton}
                        aria-selected={unit.id === generalUnitId}
                        onClick={() => dispatch({ type: "TOGGLE_GENERAL", unitId: unit.id })}
                      >
                        <GeneralMiniIcon className={unitPicker.unitKeywordIcon} />
                      </Button>

                      <Button
                        className={unitPicker.unitKeywordButton}
                        aria-selected={unit.id === battleStandardUnitId}
                        onClick={() =>
                          dispatch({ type: "TOGGLE_BATTLE_STANDARD", unitId: unit.id })
                        }
                      >
                        <BattleStandardMiniIcon className={unitPicker.unitKeywordIcon} />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </InputsGroup>
    </FormDialogV2>
  );
}
